<template>
  <div class="reset-password-form">
    <div class="mb-6 md:absolute top-8 inset-x-8">
      <Alert
        type="success"
        v-if="currentAlert === AlertEnum.RegularUser_TheFirstAttempt"
      >
        If {{ composeTransportRecipients }} associated with a valid account, a
        verification code has been sent. Check both inbox and spam folders for
        the code.
      </Alert>

      <Alert
        type="info"
        v-if="currentAlert === AlertEnum.NotMigratedUser_TheFirstAttempt"
      >
        To improve security we require that you reset your password at this
        time. As a first step, to confirm your identity, please enter the
        verification code sent to {{ composeTransportRecipients }}
      </Alert>

      <Alert
        type="success"
        v-if="currentAlert === AlertEnum.AnyUser_TheNextAttempt"
        :key="resetPasswordAttempts"
      >
        If {{ composeTransportRecipients }} associated with a valid account, a
        verification code has been sent. Check both inbox and spam folders for
        the code.
      </Alert>

      <Alert
        type="info"
        v-if="currentAlert === AlertEnum.NotMigratedUser_FromRaiseWithCode"
      >
        Please set a new password following the requirements below.
      </Alert>

      <FormError :error="error"></FormError>
    </div>

    <form
      v-if="resetState.is('STATE_SEND_CODE')"
      @submit.prevent="resetPassword()"
      novalidate
      v-keyboard-submit
    >
      <p class="mb-10 text-xs">
        Enter your email address to receive a verification code to reset your
        password.
      </p>

      <InputField
        class="block mb-4"
        label="Email"
        name="email"
        type="email"
        v-model="email"
        :validation="v$.email"
        v-autofocus
        autocomplete="email"
      ></InputField>

      <div class="text-right">
        <snap-button
          button-type="button"
          shape="circular"
          size="lg"
          variant="tertiary"
          @click="reset(), $emit('cancel')"
          class="inline-block mr-2"
          data-testid="forgot_password-cancel1"
          >Cancel
        </snap-button>

        <snap-button
          button-type="submit"
          shape="circular"
          size="lg"
          variant="primary"
          :disabled="state$.is('SUBMITTING')"
          class="inline-block"
          data-testid="forgot_password-send"
          >Send
        </snap-button>
      </div>
    </form>

    <form
      @submit.prevent="justVerifyCode()"
      novalidate
      v-if="resetState.is('STATE_ENTER_CODE')"
      v-keyboard-submit
    >
      <p class="mb-10 text-xs" v-if="!notMigrated">
        Enter the verification code we sent to your
        {{ composeTransportLabels }} in order to set your new password.
      </p>

      <p class="mb-10 text-xs" v-if="notMigrated">
        In order to set a new password, please enter the verification code we
        sent to your {{ composeTransportLabels }}.
      </p>

      <InputCodeField
        class="mb-2"
        label="Enter verification code."
        v-model="verificationCode"
        :validation="v$.verificationCode"
        help-text="Enter your 6-digit verification code."
        v-autofocus
      ></InputCodeField>

      <AvailableMethodSelector
        class="mb-2"
        :available="resetAvailableTransports"
        @select="sendCodeAgain($event)"
      ></AvailableMethodSelector>

      <div class="text-right">
        <snap-button
          button-type="button"
          shape="circular"
          size="lg"
          variant="tertiary"
          @click="cancel()"
          class="inline-block mr-2"
          data-testid="forgot_password-cancel2"
          >Cancel
        </snap-button>

        <snap-button
          button-type="submit"
          shape="circular"
          size="lg"
          variant="primary"
          :disabled="verificationCode.length < 6"
          class="inline-block"
          >Continue
        </snap-button>
      </div>
    </form>

    <form
      @submit.prevent="confirmNewPassword()"
      novalidate
      v-if="resetState.is('STATE_ENTER_PASSWORD')"
      v-keyboard-submit
    >
      <p class="mb-10 text-xs" v-if="!resetPasswordEmail">
        Enter a new password below.
      </p>

      <p class="mb-10 text-xs" v-if="resetPasswordEmail">
        Enter a new password for {{ resetPasswordEmail }} below.
      </p>

      <InputField
        class="block mb-4"
        autocomplete="off"
        label="New Password"
        name="newPassword"
        type="password"
        v-model="newPassword"
        help-text="Your password must be 8-32 characters, and include at least one lowercase letter, one uppercase letter, and a number."
        :validation="v$.newPassword"
      ></InputField>

      <InputField
        class="block mb-4"
        autocomplete="off"
        label="Confirm Password"
        name="confirmPassword"
        type="password"
        v-model="confirmPassword"
        :validation="v$.confirmPassword"
      ></InputField>

      <div class="text-right">
        <snap-button
          button-type="button"
          size="lg"
          variant="tertiary"
          @click="cancel()"
          class="inline-block mr-2"
          data-testid="forgot_password-cancel3"
          >Cancel
        </snap-button>

        <snap-button
          button-type="submit"
          size="lg"
          variant="primary"
          :disabled="state$.is('SUBMITTING')"
          class="inline-block"
          >Confirm
        </snap-button>
      </div>
    </form>
  </div>
</template>

<script lang="ts">
import { Options } from "vue-class-component";
import { mapGetters } from "vuex";

import type { User } from "@/types/graphql";
import {
  AuthenticationFlow,
  TransportEnum,
  TransportType,
} from "@/types/graphql";

import { Form, Model, Submit } from "@/core/Form";

import Alert from "@/components/snap-ui/Alert.vue";
import InputField from "@/components/snap-ui/InputField.vue";
import InputCodeField from "@/components/snap-ui/InputCodeField.vue";
import FormError from "@/components/ui/FormError.vue";
import AvailableMethodSelector from "@/components/external/AvailableMethodSelector.vue";

import { useFiniteStateMachine } from "@/core/FiniteStateMachine";
import {
  EmailRule,
  PasswordRule,
  RequiredRule,
  SameAsRule,
  ValidationCodeRule,
} from "@/util/validator";
import { authenticationFlow } from "@/util/provider";

import { $notify } from "@/services/notification";
import { confirmPassword, userResetPassword } from "@/services/user.service";
import { FeatureToggle } from "@/store/treatments.module";
import { isURL } from "@/util/helper";
import {
  composeTransportLabels,
  composeTransportRecipients,
} from "@/util/transport";
import * as FullStory from "@/util/fullStory";

enum AlertEnum {
  RegularUser_TheFirstAttempt,
  AnyUser_TheNextAttempt,
  NotMigratedUser_TheFirstAttempt,
  NotMigratedUser_FromRaiseWithCode,
}

@Options({
  emits: ["cancel"],

  components: {
    Alert,
    AvailableMethodSelector,
    InputField,
    InputCodeField,
    FormError,
  },

  computed: {
    ...mapGetters([
      "consumer",
      "featureOn",
      "me",
      "notMigrated",
      "resetAvailableTransports",
      "resetPasswordAttempts",
      "resetPasswordEmail",
      "resetUsedTransports",
    ]),
  },
})
export default class ResetPasswordForm extends Form {
  resetState = useFiniteStateMachine<string>([
    "STATE_SEND_CODE",
    "STATE_ENTER_CODE",
    "STATE_ENTER_PASSWORD",
  ]);

  consumer?: string;
  me?: User;

  resetPasswordEmail?: string;
  resetPasswordAttempts?: number;
  notMigrated?: boolean;

  resetAvailableTransports?: Array<TransportEnum>;
  resetUsedTransports?: Array<TransportType>;

  featureOn?: FeatureToggle;

  @Model({
    required: RequiredRule("Email is required."),
    email: EmailRule("Email is invalid."),
  })
  email = "";

  @Model({
    required: RequiredRule("Verification code is required."),
    code: ValidationCodeRule("Verification code is invalid."),
  })
  verificationCode = "";

  @Model({
    strong: PasswordRule(
      "Your password must be 8-32 characters, and include at least one lowercase letter, one uppercase letter, and a number."
    ),
  })
  newPassword = "";

  @Model({ sameAs: SameAsRule("Passwords don't match.", "newPassword") })
  confirmPassword = "";

  get TransportEnum() {
    return TransportEnum;
  }

  get AlertEnum() {
    return AlertEnum;
  }

  get currentAlert(): AlertEnum | undefined {
    if (
      this.state$.is("NORMAL") &&
      this.resetState.is("STATE_ENTER_CODE") &&
      !this.error &&
      this.notMigrated === false &&
      this.resetPasswordAttempts === 1
    )
      return AlertEnum.RegularUser_TheFirstAttempt;

    if (
      this.state$.is("NORMAL") &&
      this.resetState.is("STATE_ENTER_CODE") &&
      !this.error &&
      this.notMigrated === true &&
      this.resetPasswordAttempts === 1
    )
      return AlertEnum.NotMigratedUser_TheFirstAttempt;

    if (
      this.state$.is("NORMAL") &&
      this.resetState.is("STATE_ENTER_CODE") &&
      !this.error &&
      (this.resetPasswordAttempts as number) > 1
    )
      return AlertEnum.AnyUser_TheNextAttempt;

    if (
      this.state$.is("NORMAL") &&
      this.featureOn?.("USER-416-new-reset-password-flow") &&
      this.resetState.is("STATE_ENTER_PASSWORD") &&
      !this.error &&
      this.notMigrated === true &&
      this.consumer === "raise"
    )
      return AlertEnum.NotMigratedUser_FromRaiseWithCode;

    return undefined;
  }

  get composeTransportLabels(): string | undefined {
    return composeTransportLabels(this.resetUsedTransports);
  }

  get composeTransportRecipients(): string | undefined {
    return composeTransportRecipients(this.resetUsedTransports);
  }

  async beforeMount() {
    await this.$store.dispatch("useTreatments");
  }

  mounted(): void {
    if (
      this.$route.query.email &&
      !this.resetForInternal(this.$route.query.email as string)
    ) {
      const email = this.$route.query.email;

      this.$store.commit("updateResetPasswordState", {
        email,
        notMigrated: !!this.$route.query.notMigrated,
        availableTransports: [TransportEnum.Email],
        usedTransports: [
          {
            recipient: email,
            transport: TransportEnum.Email,
          },
        ],
      });
      this.resetState.set("STATE_ENTER_CODE");
    }

    if (this.$route.query.code) {
      this.verificationCode = this.$route.query.code as string;

      if (this.featureOn?.("USER-416-new-reset-password-flow")) {
        this.resetState.set("STATE_ENTER_PASSWORD");
      } else {
        this.resetState.set("STATE_ENTER_CODE");
      }
    }

    if (this.$route.query.code || this.$route.query.email) {
      this.$router.replace({ query: undefined });
    }

    if (this.resetPasswordEmail && this.resetState.is("STATE_SEND_CODE")) {
      this.resetState.set("STATE_ENTER_CODE");
    }
  }

  @Submit()
  async resetPassword(transport?: TransportEnum): Promise<void> {
    if (
      (await this.isPartialValid(["email"])) &&
      !this.resetForInternal(this.email)
    ) {
      const { availableTransport, result } = await userResetPassword({
        email: this.email,
        transport,
      });

      this.$store.commit("updateResetPasswordState", {
        email: this.email,
        availableTransports: availableTransport,
        usedTransports: result,
      });

      this.reset();
      this.resetState.set("STATE_ENTER_CODE");
    }
  }

  async sendCodeAgain(transport: TransportEnum): Promise<void> {
    if (this.resetPasswordEmail) {
      this.email = this.resetPasswordEmail;
      this.verificationCode = "";
      await this.resetPassword(transport);
    }
  }

  @Submit()
  async justVerifyCode(): Promise<void> {
    if (
      this.resetPasswordEmail &&
      (await this.isPartialValid(["verificationCode"]))
    ) {
      await confirmPassword({
        email: this.resetPasswordEmail,
        verificationCode: this.verificationCode,
      });

      this.resetState.set("STATE_ENTER_PASSWORD");
    }
  }

  @Submit()
  async confirmNewPassword(): Promise<void> {
    if (
      this.resetPasswordEmail &&
      (await this.isPartialValid(["newPassword", "confirmPassword"]))
    ) {
      const email = this.resetPasswordEmail;
      const password = this.newPassword;

      await confirmPassword({
        email: this.resetPasswordEmail,
        verificationCode: this.verificationCode,
        newPassword: this.newPassword,
      });

      FullStory.event("New Password was set", { user_email: email });

      this.$store.commit("clearResetPasswordEmail");
      // this.reset();

      await this.$store.dispatch("login", {
        email,
        password,
        consumer: this.consumer,
      });

      FullStory.event("Successful Login", { user_email: email });

      await this.$router.push({ name: "home" });

      $notify({
        title: "Your password has been updated.",
        type: "success",
      });
    }
  }

  cancel() {
    this.$store.commit("clearResetPasswordEmail");
    this.reset();
    this.resetState.set("STATE_SEND_CODE");
  }

  reset() {
    this.email = "";
    this.verificationCode = "";
    this.newPassword = "";
    this.confirmPassword = "";

    this.resetValidation([
      "email",
      "verificationCode",
      "newPassword",
      "confirmPassword",
    ]);
  }

  resetForInternal(email: string) {
    const flow: AuthenticationFlow = authenticationFlow(email);

    if (isURL(flow.redirectUrl)) {
      this.$store.dispatch("status", "redirecting");
      location.href = flow.redirectUrl as string;

      return true;
    } else {
      return false;
    }
  }
}
</script>
